import { Button, Card, TextInput } from "@tremor/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { KeyRound, Trash2, Pencil, Check, X } from "lucide-react";

import {
  deletePasskey,
  listPasskeys,
  passkeysSupported,
  registerPasskey,
  renamePasskey,
} from "../api/passkeys.js";

function formatDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export default function PasskeysSection() {
  const { t } = useTranslation("user");
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const supported = passkeysSupported();

  async function refresh() {
    setLoading(true);
    try {
      const data = await listPasskeys();
      setCredentials(data ?? []);
    } catch {
      toast.error(t("passkeys.errors.load"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (supported) refresh();
  }, [supported]);

  async function handleRegister() {
    setRegistering(true);
    try {
      await registerPasskey(newName.trim() || null);
      setNewName("");
      toast.success(t("passkeys.added"));
      await refresh();
    } catch (err) {
      const isCancel =
        err?.name === "NotAllowedError" || err?.name === "AbortError";
      toast.error(isCancel ? t("passkeys.errors.cancelled") : t("passkeys.errors.register"));
    } finally {
      setRegistering(false);
    }
  }

  async function handleRename(id) {
    const name = editName.trim();
    if (!name) return;
    try {
      await renamePasskey(id, name);
      setEditingId(null);
      await refresh();
    } catch {
      toast.error(t("passkeys.errors.rename"));
    }
  }

  async function handleDelete(id) {
    if (!confirm(t("passkeys.confirm_delete"))) return;
    try {
      await deletePasskey(id);
      toast.success(t("passkeys.removed"));
      await refresh();
    } catch {
      toast.error(t("passkeys.errors.delete"));
    }
  }

  return (
    <Card className="p-0">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-sm font-semibold text-gray-700">{t("passkeys.title")}</h2>
        <p className="mt-1 text-xs text-gray-500">{t("passkeys.description")}</p>
      </div>
      <div className="space-y-4 px-5 py-4">
        {!supported ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
            {t("passkeys.unsupported")}
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {t("passkeys.name_label")}
                </label>
                <TextInput
                  value={newName}
                  onValueChange={setNewName}
                  placeholder={t("passkeys.name_placeholder")}
                />
              </div>
              <Button
                onClick={handleRegister}
                disabled={registering}
                icon={KeyRound}
              >
                {registering ? t("passkeys.adding") : t("passkeys.add")}
              </Button>
            </div>

            {loading ? (
              <p className="text-sm text-gray-500">{t("passkeys.loading")}</p>
            ) : credentials.length === 0 ? (
              <p className="rounded-md border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                {t("passkeys.empty")}
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                {credentials.map((c) => (
                  <li
                    key={c.id}
                    className="flex items-center gap-3 px-4 py-3"
                  >
                    <KeyRound className="h-5 w-5 flex-none text-teal-600" />
                    <div className="flex-1 min-w-0">
                      {editingId === c.id ? (
                        <div className="flex gap-2">
                          <TextInput
                            value={editName}
                            onValueChange={setEditName}
                            className="flex-1"
                          />
                          <button
                            type="button"
                            onClick={() => handleRename(c.id)}
                            className="rounded p-1 text-teal-600 hover:bg-teal-50"
                            title={t("common.save", { defaultValue: "Save" })}
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className="rounded p-1 text-gray-500 hover:bg-gray-50"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <p className="truncate text-sm font-medium text-gray-800">
                            {c.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {c.backed_up
                              ? t("passkeys.synced")
                              : t("passkeys.device_bound")}
                            {c.last_used_at
                              ? ` · ${t("passkeys.last_used", { when: formatDate(c.last_used_at) })}`
                              : ` · ${t("passkeys.never_used")}`}
                          </p>
                        </>
                      )}
                    </div>
                    {editingId !== c.id && (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(c.id);
                            setEditName(c.name);
                          }}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                          title={t("passkeys.rename")}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(c.id)}
                          className="rounded p-1.5 text-red-500 hover:bg-red-50"
                          title={t("passkeys.delete")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
